import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import React from "react";
import cloneDeep from "lodash/cloneDeep";
import { connect } from "react-redux";
import Panel from "../../../components/Panel";
import StatefulButton from "../../../components/StatefulButton/StatefulButton";
import { tl } from "../../../components/translate";
import SmsTemplateBox from "../MessageTemplate/SmsTemplateBox";
import PhoneEmailMultiSelect from "./PhoneEmailMultiSelect";
import { clientFullNameSelector, clientsSortedSelector } from "../../../reducers/client";
import * as clientActions from "../../../actions/client";
import {
  proseMirrorOptions,
  RichTextfield,
  deserializeNode,
  htmlToProseMirror
} from "../../../components/RichTextfield";
import useProseMirror from "../../../components/RichTextfield/useProsemirror";
import { sendMessage } from "../../../api/campaigns";
import * as NotificationActions from "../../../actions/notification";
import { IThunkDispatch, RootState } from "../../../store";
import { Client } from "../../../interfaces/ClientInterface";
import { ReverseMap } from "../../../helpers/types";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import { Employee } from "../../../interfaces/User";
import hasOwnProperty from "../../../helpers/object";
import { salesPhoneNumber, supportEmail } from "../../../constants";

export enum MESSAGE_TYPE {
  SMS = "sms",
  EMAIL = "email"
}

export type MessageType = ReverseMap<typeof MESSAGE_TYPE>;

interface MessageCreateProps {
  onCancel: () => void;
  clients: Client[];
  actions: any;
  initialHTMLMessage?: string;
  panelTitle?: string;
  client?: Client | ServiceProvider | Employee;
  messageType?: MessageType;
  isReceiverOkhati?: boolean;
  defaultSubject?: string;
  defaultSmsBody?: string;
}

const mediaTypeList = [
  { label: "SMS", value: "sms" },
  { label: "Email", value: "email" }
];

const MessageCreate: React.FC<MessageCreateProps> = ({
  onCancel,
  clients,
  actions,
  client = undefined,
  messageType = MESSAGE_TYPE.SMS,
  initialHTMLMessage = "",
  panelTitle = "Send Message",
  isReceiverOkhati,
  defaultSubject,
  defaultSmsBody
}) => {
  // states
  const [saving, setSaving] = React.useState(false);

  const [formData, setFormData] = React.useState({
    to: [],
    subject: defaultSubject || "",
    body: defaultSmsBody || "",
    activated: true,
    messageType
  });

  const [values, setValues] = React.useState<{ name: string; val: string }[]>(
    isReceiverOkhati
      ? [
          {
            name: "Okhati",
            val:
              messageType === MESSAGE_TYPE.EMAIL
                ? supportEmail
                : `+977${salesPhoneNumber.split(",")[0].trim()}`
          }
        ]
      : []
  );

  React.useEffect(() => {
    if (!clients || !clients.length) actions.loadClients();
    if (hasOwnProperty(client || {}, "id")) {
      setValues([
        {
          name: clientFullNameSelector(client),
          val: messageType === MESSAGE_TYPE.SMS ? client.phone : client.email || "No email"
        }
      ]);
    }
  }, []);

  const [state, setState] = useProseMirror({
    ...proseMirrorOptions,
    doc: htmlToProseMirror(initialHTMLMessage)
  });

  // helpers
  const optionsForSelectPhone = (clientList: Client[] = []) =>
    clientList.map((c) => ({
      name: clientFullNameSelector(c),
      val: c.phone,
      id: c.id
    }));

  const optionsForSelectEmail = (clientList: Client[] = []) =>
    clientList
      .filter((c) => c.email)
      .map((c) => ({
        name: clientFullNameSelector(c),
        val: c.email,
        id: c.id
      }));

  return (
    <Panel
      onClose={onCancel}
      title={panelTitle}
      footer={
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button data-testmation="campaign.cancel" onClick={onCancel} style={{ marginRight: 2 }}>
              <Typography>{tl("client.cancel")}</Typography>
            </Button>
            <StatefulButton
              data-testmation="campaignSend"
              variant="contained"
              color="primary"
              disabled={saving || !values?.length}
              onClick={async () => {
                setSaving(true);
                formData.to = values.map((value) => value.val);
                if (formData.messageType === "email") {
                  formData.body = deserializeNode(state.doc);
                }
                formData.to = [...new Set(formData.to)];
                await actions.send(formData);
                setSaving(false);
                onCancel();
              }}
              isLoading={saving}
              circularProgressProps={{ size: 16 }}
            >
              <Typography>{tl("campaign.msg.send")}</Typography>
            </StatefulButton>
          </Box>
        </Box>
      }
    >
      <Box>
        <TextField
          variant="outlined"
          slotProps={{
            inputLabel: { shrink: true }
          }}
          placeholder="Type"
          select
          disabled={isReceiverOkhati}
          margin="dense"
          label={tl("campaign.msg.type")}
          style={{ flexBasis: "200px", marginRight: "10px" }}
          value={formData.messageType || ""}
          onChange={(e) => {
            const data = cloneDeep(formData);
            data.messageType = e.target.value as MESSAGE_TYPE;
            setFormData(data);
            if (client) {
              setValues([
                {
                  name: clientFullNameSelector(client),
                  val:
                    e.target.value === MESSAGE_TYPE.SMS ? client.phone : client.email || "No email"
                }
              ]);
              return;
            }
            setValues([]);
          }}
        >
          {mediaTypeList.map((media) => (
            <MenuItem key={media.value} value={media.value} id={`unqxsmi-${media.value}`}>
              {media.label}
            </MenuItem>
          ))}
        </TextField>

        <PhoneEmailMultiSelect
          type={formData.messageType}
          value={values}
          setValue={setValues}
          disabled={isReceiverOkhati}
          options={
            formData.messageType === MESSAGE_TYPE.SMS
              ? optionsForSelectPhone(clients)
              : optionsForSelectEmail(clients)
          }
        />

        {formData.messageType === MESSAGE_TYPE.SMS && (
          <SmsTemplateBox
            showCustomKeywords={false}
            content={formData.body}
            onChange={(content) => {
              const newFormData = cloneDeep(formData);
              newFormData.body = content;
              setFormData(newFormData);
            }}
          />
        )}

        {formData.messageType === MESSAGE_TYPE.EMAIL && (
          <>
            <TextField
              variant="outlined"
              slotProps={{
                inputLabel: { shrink: true }
              }}
              placeholder="Subject"
              margin="dense"
              fullWidth
              label="Subject"
              value={formData.subject}
              onChange={(e) => {
                const data = cloneDeep(formData);
                data.subject = e.target.value;
                setFormData(data);
              }}
            />
            <RichTextfield state={state} setState={setState} />
          </>
        )}
      </Box>
    </Panel>
  );
};

export default connect(
  (state: RootState) => ({
    clients: clientsSortedSelector(state)
  }),
  (dispatch: IThunkDispatch) => ({
    actions: {
      loadClients: () => dispatch(clientActions.getClients()),
      send: async (formData) => {
        await sendMessage(formData);
        dispatch(
          NotificationActions.notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "success",
            message: "Message sent",
            autoTimeout: true
          })
        );
      }
    }
  })
)(MessageCreate);
